import { Button, Dropdown } from "antd";
import { Chip } from "components/ui";
import { useEffect, useRef, useState } from "react";
import {
	approveChatAction,
	type ChatActionDeclineReason,
	type ChatActionStatus,
	conflictStatus,
	declineChatAction,
	getChatAction,
} from "services/chatActionsApi";
import { getApiErrorMessage } from "utils/apiErrorMessage";
import { useCompanyStore } from "../../../store/company.store";
import type { IActionBlock } from "./chat-block.types";

const GREEN = "var(--color-primary)";
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_MS = 10 * 60 * 1000;

/**
 * Decline reasons offered on the card. Keys are the API contract
 * (`ChatActionDeclineReason`); labels are phrased as the user's own words so
 * the distinction is obvious at a glance.
 *
 * The order is intentional: the one answer that means "the assistant got it
 * wrong" comes first, because it is the only one that is a correctness signal.
 * The rest say the proposal was fine but unwanted here.
 */
const DECLINE_REASONS: {
	key: ChatActionDeclineReason;
	label: string;
}[] = [
	{ key: "incorrect", label: "This isn't right" },
	{ key: "not_now", label: "Right, but not now" },
	{ key: "will_do_manually", label: "I'll do this myself" },
	{ key: "other", label: "Another reason" },
];

type CardPhase =
	| "pending"
	| "submitting"
	| "executing"
	| "succeeded"
	| "failed"
	| "declined"
	| "stale"
	| "expired";

function phaseFromStatus(status: ChatActionStatus): CardPhase {
	if (status === "proposed" || status === "approved") return "pending";
	return status as CardPhase;
}

function timeOfDay(iso?: string): string | null {
	if (!iso) return null;
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return null;
	return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/**
 * The confirm card — the human gate for chat-proposed money actions. The
 * preview rows are SERVER-derived (equility-api verified the amounts against
 * the ledger before this block existed); Approve echoes the idempotency key
 * so a double-click starts exactly one workflow, and the server re-validates
 * on approve (409 stale/expired render as their own states). Declining leaves
 * a visible record — the card never vanishes.
 */
export function ActionBlock({ block }: { block: IActionBlock }) {
	const [phase, setPhase] = useState<CardPhase>("pending");
	const [message, setMessage] = useState<string | null>(null);
	const inFlight = useRef(false);
	const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

	// Local expiry flip while pending — the user should never click a button
	// the server is guaranteed to 409.
	useEffect(() => {
		if (!block.expiresAt || phase !== "pending") return;
		const remaining = new Date(block.expiresAt).getTime() - Date.now();
		if (remaining <= 0) {
			setPhase("expired");
			return;
		}
		const timer = setTimeout(() => setPhase("expired"), remaining);
		return () => clearTimeout(timer);
	}, [block.expiresAt, phase]);

	useEffect(
		() => () => {
			if (pollTimer.current) clearInterval(pollTimer.current);
		},
		[],
	);

	const startPolling = (companyId: number) => {
		const startedAt = Date.now();
		if (pollTimer.current) clearInterval(pollTimer.current);
		pollTimer.current = setInterval(async () => {
			if (Date.now() - startedAt > POLL_MAX_MS) {
				if (pollTimer.current) clearInterval(pollTimer.current);
				setMessage("Still running — check back in a bit.");
				return;
			}
			try {
				const state = await getChatAction(block.proposalId, companyId);
				if (state.status === "succeeded" || state.status === "failed") {
					if (pollTimer.current) clearInterval(pollTimer.current);
					setPhase(state.status);
					if (state.error) setMessage(state.error);
				}
			} catch {
				// Transient poll failure: keep polling until the budget runs out.
			}
		}, POLL_INTERVAL_MS);
	};

	const handleApprove = async () => {
		if (inFlight.current) return;
		inFlight.current = true;
		setPhase("submitting");
		const companyId = useCompanyStore.getState().id;
		if (companyId == null) {
			inFlight.current = false;
			setPhase("failed");
			setMessage("No company context — reload and try again.");
			return;
		}
		try {
			const result = await approveChatAction(
				block.proposalId,
				companyId,
				block.idempotencyKey,
			);
			setPhase(phaseFromStatus(result.status));
			if (result.error) setMessage(result.error);
			if (result.status === "executing") startPolling(companyId);
		} catch (error) {
			const conflict = conflictStatus(error);
			if (conflict === "stale") {
				setPhase("stale");
				setMessage(
					"This data changed since the proposal — ask again to get a fresh one.",
				);
			} else if (conflict === "expired") {
				setPhase("expired");
			} else if (conflict === "executing" || conflict === "succeeded") {
				setPhase(conflict);
				if (conflict === "executing") startPolling(companyId);
			} else if (conflict === "declined") {
				setPhase("declined");
			} else {
				setPhase("failed");
				setMessage(
					(error as { response?: unknown })?.response
						? getApiErrorMessage(error)
						: "The action could not be started.",
				);
			}
		} finally {
			inFlight.current = false;
		}
	};

	const handleDecline = async (reason?: ChatActionDeclineReason) => {
		if (inFlight.current) return;
		inFlight.current = true;
		const companyId = useCompanyStore.getState().id;
		try {
			if (companyId != null) {
				await declineChatAction(block.proposalId, companyId, reason);
			}
			setPhase("declined");
		} catch (error) {
			const conflict = conflictStatus(error);
			setPhase(conflict ? phaseFromStatus(conflict) : "declined");
		} finally {
			inFlight.current = false;
		}
	};

	const asOf = timeOfDay(block.asOf);
	const statusTag = () => {
		switch (phase) {
			case "executing":
				return <Chip variant="blue">Running…</Chip>;
			case "succeeded":
				return <Chip variant="green">Done</Chip>;
			case "failed":
				return <Chip variant="red">Failed</Chip>;
			case "declined":
				return <Chip variant="grey">You declined this action</Chip>;
			case "stale":
				return <Chip variant="warning">Changed since proposed</Chip>;
			case "expired":
				return <Chip variant="grey">Proposal expired</Chip>;
			default:
				return null;
		}
	};

	return (
		// role="status" makes the card a polite live region so the outcome of an
		// approve/decline — the status chip, the 3s poll flipping to Done/Failed,
		// and the 409 conflict messages — is announced instead of only appearing.
		// Matches the dashboard's `dash-error-banner`. The region has to be in the
		// DOM before the change, which it is: the card mounts in "pending".
		<div
			role="status"
			style={{
				border: `1px solid ${phase === "pending" || phase === "submitting" ? GREEN : "rgba(0,0,0,0.1)"}`,
				borderRadius: 8,
				padding: 12,
				background: "#fff",
				maxWidth: 420,
			}}
		>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "baseline",
					gap: 8,
				}}
			>
				<div style={{ fontWeight: 600, fontSize: 13 }}>{block.title}</div>
				{statusTag()}
			</div>
			{block.summaryAmount ? (
				<div
					style={{
						fontSize: 20,
						fontWeight: 700,
						fontVariantNumeric: "tabular-nums",
						margin: "6px 0",
					}}
				>
					{block.summaryAmount}
				</div>
			) : null}
			<table style={{ width: "100%", fontSize: 11.5, borderSpacing: 0 }}>
				<tbody>
					{block.preview.map((row) => (
						<tr key={row.label}>
							<td
								style={{ color: "rgba(0,0,0,0.55)", padding: "2px 8px 2px 0" }}
							>
								{row.label}
							</td>
							<td
								style={{
									textAlign: "right",
									fontVariantNumeric: "tabular-nums",
									padding: "2px 0",
								}}
							>
								{row.detail}
							</td>
						</tr>
					))}
				</tbody>
			</table>
			{asOf ? (
				<div
					style={{ fontSize: 10.5, color: "rgba(0,0,0,0.45)", marginTop: 4 }}
				>
					as of {asOf}
				</div>
			) : null}
			{message ? (
				<div
					style={{ fontSize: 11.5, color: "rgba(0,0,0,0.65)", marginTop: 6 }}
				>
					{message}
				</div>
			) : null}
			{(phase === "pending" || phase === "submitting") && (
				<div style={{ display: "flex", gap: 8, marginTop: 10 }}>
					<Button
						type="primary"
						size="small"
						loading={phase === "submitting"}
						disabled={phase === "submitting"}
						onClick={() => handleApprove()}
					>
						Approve
					</Button>
					{/*
					  Deliberately menu-only: there is no plain one-click decline.
					  An always-available "Other reason" keeps the escape hatch free,
					  and if the primary button declined without a reason almost
					  everyone would take it, leaving the column NULL and the whole
					  reason enum capturing nothing.
					*/}
					<Dropdown
						trigger={["click"]}
						disabled={phase === "submitting"}
						menu={{
							items: DECLINE_REASONS.map((r) => ({
								key: r.key,
								label: r.label,
							})),
							onClick: ({ key }) =>
								handleDecline(key as ChatActionDeclineReason),
						}}
					>
						<Button size="small" disabled={phase === "submitting"}>
							Decline
						</Button>
					</Dropdown>
				</div>
			)}
		</div>
	);
}
